import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, PenLine, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const BUDGET_OPTIONS = [
  { v: "300-500", label: "$300 – $500" },
  { v: "500-800", label: "$500 – $800" },
  { v: "800-1200", label: "$800 – $1,200" },
  { v: "1200-1500", label: "$1,200 – $1,500" },
  { v: "over-1500", label: "Over $1,500" },
];

const Commission = () => {
  const [form, setForm] = useState({
    name: "", email: "", budget_range: "800-1200", group_size: 10,
    event_date: "", theme: "", note: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/commission/inquire", {
        ...form, group_size: parseInt(form.group_size, 10) || 2,
      });
      setSent(data.commission_id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send inquiry.");
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <div className="grain min-h-screen flex items-center justify-center">
        <div className="card-noir p-8 max-w-md text-center" data-testid="commission-sent">
          <CheckCircle2 size={32} className="mx-auto text-[#4ade80] mb-3" />
          <div className="font-display text-2xl text-[#f4f0ec] mb-2">Got it.</div>
          <div className="text-sm text-[#a39797] mb-3">
            We'll come back with a quote within 48 hours.
            <br/>Reference: <span className="font-mono text-[#d4af37]" translate="no">{sent.toUpperCase()}</span>
          </div>
          <Link to="/" className="btn-ghost text-sm">Back home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="grain min-h-screen" data-testid="commission-page">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>

        <div className="mb-8">
          <div className="font-hand text-[#d4af37] rotate-[-1deg] text-lg mb-2">commission a one-of-one</div>
          <h1 className="font-display text-5xl text-[#f4f0ec] mb-3">Hire me to write your mystery.</h1>
          <p className="text-[#c7bebe] text-lg">
            Wedding. Bachelorette. Big birthday. Company offsite. I write it to the group —
            inside jokes, real names, your choice of culprit.
          </p>
          <p className="text-[#a39797] text-sm mt-3">
            Budgets $300 – $1,500. 2-3 week turnaround.
          </p>
        </div>

        <form onSubmit={submit} className="card-noir p-6 space-y-3" data-testid="commission-form">
          <div className="grid md:grid-cols-2 gap-3">
            <input required placeholder="Your name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="input-noir" data-testid="commission-name" />
            <input required type="email" placeholder="Your email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-noir" data-testid="commission-email" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <select value={form.budget_range}
              onChange={(e) => setForm({ ...form, budget_range: e.target.value })}
              className="input-noir" data-testid="commission-budget">
              {BUDGET_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
            <input type="number" min="2" max="200" placeholder="Group size" value={form.group_size}
              onChange={(e) => setForm({ ...form, group_size: e.target.value })}
              className="input-noir" data-testid="commission-group-size" />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <input type="date" value={form.event_date}
              onChange={(e) => setForm({ ...form, event_date: e.target.value })}
              className="input-noir" data-testid="commission-date" />
            <input placeholder="Theme / vibe (optional)" value={form.theme}
              onChange={(e) => setForm({ ...form, theme: e.target.value })}
              className="input-noir" data-testid="commission-theme" />
          </div>
          <textarea required minLength={10} maxLength={1200}
            placeholder="Tell me about the group. Inside jokes, who's the obvious suspect, anything off-limits."
            value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="input-noir min-h-[160px]" data-testid="commission-note" />
          <button type="submit" disabled={busy} className="btn-primary w-full justify-center"
            data-testid="commission-submit">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <PenLine size={14} />}
            Send inquiry
          </button>
          <div className="text-[11px] text-[#5a5050] text-center">
            I read every inquiry personally. Expect a reply within 48 hours.
          </div>
        </form>
      </div>
    </div>
  );
};

export default Commission;
